/**
 * AI Copilot tool surface (Anthropic tool-use format).
 *
 * Every tool is a deterministic, idempotent action on the configurator's
 * state. The dispatcher in `executeTool()` returns a JSON-serialisable result
 * the LLM can read back ("ok: true / message" or "ok: false / error").
 *
 * The model sees the catalog through state-summary in the system prompt + the
 * tool surface here. No tool reads back the catalog directly — the model
 * already has structural knowledge in its system prompt.
 */

import {
  COLLECTIONS, COLLECTION_BY_CODE, BASE_FINISHES, BASE_BY_CODE,
  SIZES_BY_COLLECTION, CHAIR_COLLECTIONS, CHAIR_FRAMES, CHAIR_SIZES,
  availableBases, CERAMIC_AVAILABILITY,
  chairUpholsteryGroup,
} from '../data/catalog.js';

// ─── Validators ────────────────────────────────────────────────────────────
// Return null on success, or an error string the LLM can read and correct from.

function validateTableArgs({ collection, size_key, top_code, base_code, insert_or_profile }) {
  if (collection && !SIZES_BY_COLLECTION[collection]) {
    return `Unknown collection "${collection}". Valid: ${Object.keys(SIZES_BY_COLLECTION).join(', ')}.`;
  }
  if (size_key) {
    if (!collection) return 'When size_key is given, collection must also be given.';
    const sizes = SIZES_BY_COLLECTION[collection] || [];
    const size = sizes.find(s => s.key === size_key);
    if (!size) {
      return `sizeKey "${size_key}" not available in ${collection}. Valid sizes for ${collection}: ${sizes.map(s => s.key).join(', ')}.`;
    }
    if (top_code && !size.tops.includes(top_code)) {
      return `topCode "${top_code}" not available on ${collection} ${size_key}. Valid tops: ${size.tops.join(', ')}.`;
    }
  }
  if (base_code && !BASE_BY_CODE[base_code]) {
    return `Unknown baseCode "${base_code}". Valid: ${BASE_FINISHES.map(b => b.code).join(', ')}.`;
  }
  // RND ø140 wood — tier A only
  if (collection === 'RND' && size_key === 'ø140' && (top_code === 'NC' || top_code === 'RB') && base_code) {
    const tier = BASE_BY_CODE[base_code]?.tier;
    if (tier !== 'A') return 'RND ø140 with NC/RB wood top supports tier-A bases only (GFM69 or GFM73).';
  }
  return null;
}

function validateChairArgs({ collection, size_key, frame_code, upholstery_code }) {
  if (collection && !CHAIR_FRAMES[collection]) {
    return `Unknown chair collection "${collection}". Valid: ${Object.keys(CHAIR_FRAMES).join(', ')}.`;
  }
  if (size_key && !CHAIR_SIZES.find(s => s.key === size_key)) {
    return `Invalid size_key "${size_key}". Valid: ${CHAIR_SIZES.map(s => s.key).join(', ')}.`;
  }
  if (frame_code && collection) {
    const ok = CHAIR_FRAMES[collection].some(f => f.code === frame_code);
    if (!ok) return `frame_code "${frame_code}" not valid for ${collection}. Valid: ${CHAIR_FRAMES[collection].map(f => f.code).join(', ')}.`;
  }
  if (upholstery_code && !chairUpholsteryGroup(upholstery_code) && upholstery_code !== 'COM' && upholstery_code !== 'COL') {
    return `upholstery_code "${upholstery_code}" not recognized. See system prompt for groups.`;
  }
  return null;
}

// patches use camelCase keys (sizeKey, baseCode, topCode, frameCode, upholsteryCode, insertOrProfile, qty).
function patchToSnake(patch) {
  return {
    collection:        patch.collection,
    size_key:          patch.sizeKey,
    top_code:          patch.topCode,
    base_code:         patch.baseCode,
    insert_or_profile: patch.insertOrProfile,
    frame_code:        patch.frameCode,
    upholstery_code:   patch.upholsteryCode,
  };
}

export const TOOLS = [
  {
    name: 'add_table',
    description: 'Append a new SKORPIO table item to the project. Returns the new item id. Use defaults sparingly — prefer to set collection/size/base/top explicitly when the user asked for something specific.',
    input_schema: {
      type: 'object',
      properties: {
        collection:       { type: 'string', enum: COLLECTIONS.map(c => c.code), description: 'CL classic glass, CA Crystalart, KR Keramik, KP Keramik Premium, KPR Keramik Premium Round, KRD Keramik Round, KW Ker-Wood Round, RND Round, WD Wood.' },
        size_key:         { type: 'string', description: 'Size key like "200x100", "sag.240x120", "polygon.300x130", "ø160", "S.300x120". Must match the collection.' },
        base_code:        { type: 'string', enum: BASE_FINISHES.map(b => b.code), description: 'GFM69/GFM73 tier A, GFM11/GFM18/GFM70 tier B, BBR/BGR tier C.' },
        top_code:         { type: 'string', description: 'Top finish code: CLR/EXC/EXCB/EXCBR/MST for glass, CY01/CY02 for Crystalart, KM02..KS23 for ceramic, NC/RB/RN for wood.' },
        insert_or_profile:{ type: 'string', description: 'For KW: ceramic insert (KM*). For KP/KPR: brushed profile (BBR or BGR).' },
        qty:              { type: 'integer', minimum: 1, default: 1 },
      },
      required: ['collection'],
    },
  },
  {
    name: 'add_chair',
    description: 'Append a new BELINDA chair item. Defaults: BL collection, "reg" size, frN frame, soft leather Bianco (971), qty 6.',
    input_schema: {
      type: 'object',
      properties: {
        collection:      { type: 'string', enum: CHAIR_COLLECTIONS.map(c => c.code), description: 'BL = BELINDA wood frame, BLM = BELINDA ML metal frame.' },
        size_key:        { type: 'string', enum: ['reg', 'B'], description: '"reg" without armrests, "B" with armrests.' },
        frame_code:      { type: 'string', description: 'BL: frN/frNC/frRB/fr73. BLM: GFM11/GFM18/GFM69/GFM73.' },
        upholstery_code: { type: 'string', description: 'B-fabric codes, MN micro nubuck, ECP synthetic leather, numeric soft leather, GLV glove leather, or COM/COL for customer-supplied.' },
        qty:             { type: 'integer', minimum: 1, default: 6 },
      },
      required: ['collection'],
    },
  },
  {
    name: 'update_item',
    description: 'Patch fields on an existing item. Provide only the fields to change; others remain. For tables: collection, sizeKey, baseCode, topCode, insertOrProfile, qty. For chairs: collection, sizeKey, frameCode, upholsteryCode, qty.',
    input_schema: {
      type: 'object',
      properties: {
        item_id: { type: 'integer', description: 'Numeric id of the item to modify (see state summary).' },
        patch:   { type: 'object', description: 'Object of fields to overwrite. Example: {"baseCode":"BBR"}.' },
      },
      required: ['item_id', 'patch'],
    },
  },
  {
    name: 'remove_item',
    description: 'Delete an item from the project.',
    input_schema: {
      type: 'object',
      properties: { item_id: { type: 'integer' } },
      required: ['item_id'],
    },
  },
  {
    name: 'set_quantity',
    description: 'Shortcut to change item.qty.',
    input_schema: {
      type: 'object',
      properties: {
        item_id: { type: 'integer' },
        qty:     { type: 'integer', minimum: 1 },
      },
      required: ['item_id', 'qty'],
    },
  },
  {
    name: 'select_item',
    description: 'Move the selection cursor to a given item — its config editor and Item-spec panel update accordingly. Call this when narrating which item you just changed.',
    input_schema: {
      type: 'object',
      properties: { item_id: { type: 'integer' } },
      required: ['item_id'],
    },
  },
];

/**
 * Execute a tool by name. Receives setter callbacks from React parent
 * (closure-captured to keep React state up-to-date). Returns a simple
 * { ok, message?, error?, ... } object that round-trips back to the model.
 */
export function makeToolExecutor({ addTable, addChair, updateItem, removeItem, selectItem, getItems }) {
  return (name, args) => {
    try {
      switch (name) {
        case 'add_table': {
          const err = validateTableArgs(args || {});
          if (err) return { ok: false, error: err };
          const id = addTable(args || {});
          return { ok: true, message: `Added table (id ${id}).`, item_id: id };
        }
        case 'add_chair': {
          const err = validateChairArgs(args || {});
          if (err) return { ok: false, error: err };
          const id = addChair(args || {});
          return { ok: true, message: `Added chair (id ${id}).`, item_id: id };
        }
        case 'update_item': {
          if (!args.item_id || !args.patch) return { ok: false, error: 'item_id and patch are required' };
          // Look up the current item to know its kind, then validate the merged patch.
          const items = getItems ? getItems() : [];
          const current = items.find(it => it.id === args.item_id);
          if (!current) return { ok: false, error: `No item with id ${args.item_id} exists. Valid ids: ${items.map(i => i.id).join(', ') || 'none'}.` };
          const merged = { ...current, ...args.patch };
          const snake = patchToSnake(merged);
          const err = current.kind === 'table' ? validateTableArgs(snake) : validateChairArgs(snake);
          if (err) return { ok: false, error: err };
          updateItem(args.item_id, args.patch);
          return { ok: true, message: `Updated item ${args.item_id}.` };
        }
        case 'remove_item': {
          if (!args.item_id) return { ok: false, error: 'item_id required' };
          removeItem(args.item_id);
          return { ok: true, message: `Removed item ${args.item_id}.` };
        }
        case 'set_quantity': {
          if (!args.item_id || !args.qty) return { ok: false, error: 'item_id and qty required' };
          updateItem(args.item_id, { qty: args.qty });
          return { ok: true, message: `Set qty=${args.qty} on item ${args.item_id}.` };
        }
        case 'select_item': {
          if (!args.item_id) return { ok: false, error: 'item_id required' };
          selectItem(args.item_id);
          return { ok: true };
        }
        default:
          return { ok: false, error: `Unknown tool ${name}` };
      }
    } catch (e) {
      return { ok: false, error: String(e?.message || e) };
    }
  };
}
